import { useState } from 'react';
import type { Adventure, FieldDescriptor, KindDef } from '@gm-binder/shared';
import { parseRef } from '@gm-binder/shared';
import { Field, Input, Textarea, Select, Switch, MediaImage } from '../ui';
import { mediaUrl, api } from '../../api/client';
import { RefPicker } from '../common/RefPicker';
import { roleField } from '../../lib/viewRegistry';
import styles from './EntityForm.module.css';

type Entity = Record<string, unknown>;

/**
 * Stored references use bare ids (e.g. "cucina"), matching the content files and the runtime
 * lookups (`r.id === room`). RefPicker emits a namespaced "kind:id" ref on pick, so we strip the
 * namespace before storing to keep both in sync.
 */
const bareId = (ref: string): string => parseRef(ref)?.id ?? ref;

export interface EntityFormProps {
  kind: KindDef;
  value: Entity;
  adventure: Adventure;
  slug: string;
  onChange: (next: Entity) => void;
}

/** Tag-style multi-string editor (chips + add input). */
function TagsInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setDraft('');
  };
  return (
    <div className={styles.tags}>
      {value.map((t) => (
        <span key={t} className={styles.tag}>
          {t}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== t))} aria-label="Rimuovi">
            ×
          </button>
        </span>
      ))}
      <input
        className={styles.tagInput}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
        placeholder="Aggiungi…"
      />
    </div>
  );
}

/** Multi-reference editor: a list of RefPickers plus an add slot. */
function RefListInput({
  value,
  onChange,
  adventure,
  target,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  adventure: Adventure;
  target?: string | string[];
}) {
  return (
    <div className={styles.refList}>
      {value.map((ref, i) => (
        <div key={`${ref}-${i}`} className={styles.refRow}>
          <RefPicker
            adventure={adventure}
            value={ref}
            target={target}
            onChange={(next) => {
              const copy = [...value];
              if (next) copy[i] = bareId(next);
              else copy.splice(i, 1);
              onChange(copy);
            }}
          />
        </div>
      ))}
      <RefPicker
        adventure={adventure}
        value={null}
        target={target}
        placeholder="Aggiungi riferimento…"
        onChange={(next) => {
          const id = next ? bareId(next) : null;
          if (id && !value.includes(id)) onChange([...value, id]);
        }}
      />
    </div>
  );
}

/** Reference image: live preview + file upload (stored under the adventure's media) + manual path. */
export function ImageInput({
  slug,
  value,
  label,
  onChange,
}: {
  slug: string;
  value: string;
  label: string;
  onChange: (v: string | undefined) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const { path } = await api.uploadImage(slug, file);
      onChange(path);
    } catch {
      setError('Caricamento non riuscito');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.image}>
      {value && (
        <MediaImage src={mediaUrl(slug, value)} alt={label} className={styles.imagePreview} />
      )}
      <div className={styles.imageControls}>
        <label className={styles.uploadBtn} data-busy={busy || undefined}>
          {busy ? 'Caricamento…' : '⬆ Carica immagine'}
          <input
            type="file"
            accept="image/*"
            hidden
            disabled={busy}
            onChange={(e) => {
              void upload(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
        {value && (
          <button type="button" className={styles.clearBtn} onClick={() => onChange(undefined)}>
            Rimuovi
          </button>
        )}
      </div>
      <Input
        value={value}
        placeholder="percorso o URL immagine"
        onChange={(e) => onChange(e.target.value || undefined)}
      />
      {error && <span className={styles.jsonError}>{error}</span>}
    </div>
  );
}

type RefCount = { id: string; count: number };

/** Reference-with-quantity editor: pick an entity and how many of it (e.g. 3× goblin). */
function RefCountInput({
  value,
  onChange,
  adventure,
  target,
}: {
  value: RefCount[];
  onChange: (v: RefCount[]) => void;
  adventure: Adventure;
  target?: string | string[];
}) {
  const setCount = (i: number, count: number) => {
    const copy = [...value];
    copy[i] = { ...copy[i], count: Math.max(1, count) };
    onChange(copy);
  };
  return (
    <div className={styles.refList}>
      {value.map((row, i) => (
        <div key={`${row.id}-${i}`} className={styles.refCountRow}>
          <RefPicker
            adventure={adventure}
            value={row.id}
            target={target}
            onChange={(next) => {
              const copy = [...value];
              if (next) copy[i] = { ...copy[i], id: bareId(next) };
              else copy.splice(i, 1);
              onChange(copy);
            }}
          />
          <div className={styles.counter}>
            <button type="button" onClick={() => setCount(i, (row.count || 1) - 1)} aria-label="Meno">
              −
            </button>
            <input
              type="number"
              min={1}
              value={row.count || 1}
              onChange={(e) => setCount(i, Number(e.target.value) || 1)}
            />
            <button type="button" onClick={() => setCount(i, (row.count || 1) + 1)} aria-label="Più">
              ＋
            </button>
          </div>
        </div>
      ))}
      <RefPicker
        adventure={adventure}
        value={null}
        target={target}
        placeholder="Aggiungi mostro…"
        onChange={(next) => {
          const id = next ? bareId(next) : null;
          if (id && !value.some((r) => r.id === id)) onChange([...value, { id, count: 1 }]);
        }}
      />
    </div>
  );
}

/** JSON editor for structured field types (statblock/rows) — commits only valid JSON. */
function JsonInput({ value, onChange }: { value: unknown; onChange: (v: unknown) => void }) {
  const [text, setText] = useState(() => JSON.stringify(value ?? null, null, 2));
  const [error, setError] = useState<string | null>(null);
  return (
    <div>
      <Textarea
        rows={8}
        value={text}
        spellCheck={false}
        className={styles.mono}
        onChange={(e) => {
          setText(e.target.value);
          try {
            onChange(e.target.value.trim() ? JSON.parse(e.target.value) : null);
            setError(null);
          } catch {
            setError('JSON non valido');
          }
        }}
      />
      {error && <span className={styles.jsonError}>{error}</span>}
    </div>
  );
}

function FieldInput({
  field,
  value,
  adventure,
  slug,
  onChange,
}: {
  field: FieldDescriptor;
  value: unknown;
  adventure: Adventure;
  slug: string;
  onChange: (v: unknown) => void;
}) {
  switch (field.type) {
    case 'markdown':
      return (
        <Textarea
          rows={6}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          value={value == null || value === '' ? '' : Number(value)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
      );
    case 'boolean':
      return <Switch checked={Boolean(value)} onChange={(c) => onChange(c)} />;
    case 'select':
      return (
        <Select value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || undefined)}>
          <option value="">—</option>
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>
      );
    case 'tags':
      return (
        <TagsInput
          value={Array.isArray(value) ? value.map(String) : []}
          onChange={(v) => onChange(v)}
        />
      );
    case 'image':
      return (
        <ImageInput
          slug={slug}
          value={typeof value === 'string' ? value : ''}
          label={field.label}
          onChange={onChange}
        />
      );
    case 'ref':
      return (
        <RefPicker
          adventure={adventure}
          value={typeof value === 'string' ? value : null}
          target={field.target}
          onChange={(ref) => onChange(ref ? bareId(ref) : undefined)}
        />
      );
    case 'refList':
      return (
        <RefListInput
          value={Array.isArray(value) ? value.map(String) : []}
          onChange={(v) => onChange(v)}
          adventure={adventure}
          target={field.target}
        />
      );
    case 'refCount':
      return (
        <RefCountInput
          value={
            Array.isArray(value)
              ? (value as unknown[])
                  .map((v) => {
                    const o = (v ?? {}) as { id?: unknown; count?: unknown };
                    return { id: String(o.id ?? ''), count: Number(o.count) || 1 };
                  })
                  .filter((v) => v.id)
              : []
          }
          onChange={(v) => onChange(v)}
          adventure={adventure}
          target={field.target}
        />
      );
    case 'statblock':
    case 'rows':
      return <JsonInput value={value} onChange={onChange} />;
    case 'text':
    default:
      return (
        <Input
          value={typeof value === 'string' ? value : value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

/** Authoring form for a single entity, driven entirely by the kind's field descriptors. */
export function EntityForm({ kind, value, adventure, slug, onChange }: EntityFormProps) {
  const bodyKey = roleField(kind, 'body')?.key;
  // Body field is rendered last and full-width for comfortable prose editing.
  const ordered = [...kind.fields].sort((a, b) => {
    if (a.key === bodyKey) return 1;
    if (b.key === bodyKey) return -1;
    return 0;
  });

  // Wide controls span the full form width so short scalars pair up cleanly beside each other.
  const wideTypes = new Set(['markdown', 'image', 'refList', 'refCount', 'statblock', 'rows', 'tags']);

  return (
    <div className={styles.form}>
      {ordered.map((field) => (
        <Field
          key={field.key}
          label={field.label + (field.required ? ' *' : '')}
          hint={field.help}
          className={field.key === bodyKey || wideTypes.has(field.type) ? styles.full : undefined}
        >
          <FieldInput
            field={field}
            value={value[field.key]}
            adventure={adventure}
            slug={slug}
            onChange={(v) => onChange({ ...value, [field.key]: v })}
          />
        </Field>
      ))}
    </div>
  );
}
